import {
	getProviderLabel,
	useProviders,
	type ProviderWithReceiver,
} from "./useProvider";
import { queries } from "@loanlink-nl/zero";
import { computed } from "vue";
import { useQuery } from "./useZero";
import type { LenderRecord } from "./useLender";

export type LenderEntry = LenderRecord & {
	provider: ProviderWithReceiver;
};

type QueryRows<T> = readonly T[] | Record<string, T> | null | undefined;

export type LenderOption = {
	value: string;
	label: string;
	lender: LenderEntry;
	icon?: string;
	logo?: string;
};

function toRows<T>(rows: QueryRows<T>): T[] {
	if (!rows) {
		return [];
	}

	return Array.isArray(rows) ? [...rows] : Object.values(rows);
}

export function useProviderLenders() {
	const { providersByCode } = useProviders();
	const { data: lenders } = useQuery(() => queries.lenders.all());
	const lenderList = computed(() =>
		toRows<LenderRecord>(lenders.value as QueryRows<LenderRecord>),
	);

	const lendersByProviderCode = computed(() => {
		return new Map(
			lenderList.value.flatMap((lender) => {
				if (!lender.hdnProviderCode) {
					return [];
				}

				return [[lender.hdnProviderCode, lender] as const];
			}),
		);
	});

	const lendersWithProvider = computed<LenderEntry[]>(() => {
		return lenderList.value.flatMap((lender) => {
			if (!lender.hdnProviderCode) {
				return [];
			}

			const provider = providersByCode.value.get(lender.hdnProviderCode);
			if (!provider || !provider.mortgage || !provider.isActive) {
				return [];
			}

			return [{ ...lender, provider } satisfies LenderEntry];
		});
	});

	const lenderOptions = computed<LenderOption[]>(() => {
		return lendersWithProvider.value
			.map((lender) => {
				const provider = lender.provider;

				return {
					value: provider.name,
					label: getProviderLabel(provider),
					lender,
					icon: lender.icon ?? undefined,
					logo: lender.logo ?? undefined,
				};
			})
			.toSorted((a, b) => a.label.localeCompare(b.label));
	});

	function getLenderAsset(value?: string) {
		if (!value) {
			return undefined;
		}

		return lendersByProviderCode.value.get(value);
	}

	function getLenderOption(value?: string) {
		if (!value) {
			return undefined;
		}

		return lenderOptions.value.find((option) => option.value === value);
	}

	return {
		lenderOptions,
		getLenderAsset,
		getLenderOption,
	};
}
